/* eslint-disable no-underscore-dangle, no-else-return */
import { PostHogProvider as BasePostHogProvider } from '@posthog/react';
import {
  type CaptureOptions,
  type CaptureResult,
  type PostHog,
  type Properties,
  posthog,
} from 'posthog-js';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import {
  POSTHOG_API_KEY,
  POSTHOG_DEBUG,
  POSTHOG_HOST,
} from '../config/runtime-env';

import type { AnalyticsEvent } from '../utils/analytics';
import { sanitizePostHogEvent } from '../utils/posthog-sanitize';
import { mapEnvironment } from '../utils/standard-event-properties';

// Automatic events ($pageview on every history change) go straight through the
// SDK and skip the root provider's properties, so every event gets
// `environment` here. An event that already names one keeps it.
function prepareEvent(event: CaptureResult | null): CaptureResult | null {
  return sanitizePostHogEvent(
    event && {
      ...event,
      properties: { environment: mapEnvironment(), ...event.properties },
    },
  );
}

// Skip init entirely when no token is configured — posthog-js bails out on an
// empty token anyway, and initializing unconditionally means a half-configured
// build reaches code paths that assume a valid config.
if (POSTHOG_API_KEY) {
  posthog.init(POSTHOG_API_KEY, {
    // Coerce a missing host to `undefined` so posthog-js keeps its own default
    // host. Its config merge only skips `undefined`, so the empty string that
    // `runtime-env` falls back to for an unset VITE_POSTHOG_HOST would
    // overwrite the default with an unusable host instead of falling back.
    api_host: POSTHOG_HOST || undefined,
    // `defaults` is a PostHog-originated identifier for a bundle of settings
    // defined as-of a particular date, in our case the defaults as of 2025-11-30
    defaults: '2025-11-30',
    // only capture events after we call `identify`
    person_profiles: 'identified_only',
    // No PostHog flag is read here. Together these stop every /flags
    // request, including the one remote config triggers on load.
    advanced_disable_feature_flags: true,
    advanced_disable_feature_flags_on_first_load: true,
    // enables autocapturing $pageview events on SPAs by hooking into history
    // change events
    capture_pageview: 'history_change',
    capture_pageleave: false,
    autocapture: {
      // disable autocapturing DOM events
      dom_event_allowlist: [],
    },
    before_send: prepareEvent,
    debug: POSTHOG_DEBUG === 'true',
  });
}

export interface PostHogProxy extends PostHog {
  _rawClient: PostHog;
  _defaultProperties: Properties | undefined | null;

  capture<S extends string>(
    name: AnalyticsEvent<S>,
    properties?: Properties | null,
    options?: CaptureOptions,
  ): CaptureResult | undefined;
}

export interface PostHogWithDefaults {
  posthog: PostHogProxy;
}

export const PostHogContext = createContext<PostHogWithDefaults>({
  posthog: makeProxy(posthog, {}),
});

export interface PostHogProviderProps {
  properties?: Properties;
  children: ReactNode;
}

export function PostHogRootProvider({
  properties = {},
  children,
}: PostHogProviderProps) {
  const wrapped = useMemo(() => wrapPostHog(posthog, properties), [properties]);

  return (
    <PostHogContext.Provider
      value={{
        posthog: wrapped,
      }}
    >
      <BasePostHogProvider client={wrapped}>{children}</BasePostHogProvider>
    </PostHogContext.Provider>
  );
}

export function PostHogPropertiesProvider({
  properties = {},
  children,
}: PostHogProviderProps) {
  const { posthog } = useContext(PostHogContext);
  const wrapped = useMemo(
    () => wrapPostHog(posthog, properties),
    [posthog, properties],
  );

  return (
    <PostHogContext.Provider
      value={{
        posthog: wrapped,
      }}
    >
      <BasePostHogProvider client={wrapped}>{children}</BasePostHogProvider>
    </PostHogContext.Provider>
  );
}

export function wrapPostHog(
  posthog: PostHog | PostHogProxy,
  properties?: Properties,
): PostHogProxy {
  if ((posthog as PostHogProxy)._rawClient !== undefined) {
    const cast = posthog as PostHogProxy;

    return makeProxy(cast._rawClient, {
      ...(cast._defaultProperties ?? {}),
      ...(properties ?? {}),
    });
  } else {
    return makeProxy(posthog, properties ?? {});
  }
}

function makeProxy(posthog: PostHog, defaults: Properties): PostHogProxy {
  function capture<S extends string>(
    name: AnalyticsEvent<S>,
    properties?: Properties | null,
    options?: CaptureOptions,
  ): CaptureResult | undefined {
    const mergedProperties =
      properties === null ? null : { ...defaults, ...(properties ?? {}) };
    return posthog.capture(name, mergedProperties, options);
  }

  const handlers = {
    get(target: PostHog, prop: string | symbol, receiver: unknown): unknown {
      if (prop === 'capture') {
        return capture;
      } else if (prop === '_rawClient') {
        return target;
      } else if (prop === '_defaultProperties') {
        return defaults as unknown;
      }

      return Reflect.get(target, prop, receiver) as unknown;
    },
  };

  return new Proxy(posthog, handlers) as PostHogProxy;
}
